import { ApiMessage } from '@app/types/api'

export interface SugarRequest {
  cognitoId: string
}

export interface SugarPaginatedRequest extends SugarRequest {
  page?: number | string
  limit?: number | string
}

export interface SugarResponse<T> {
  data: T
  messages?: ApiMessage[]
  success: boolean
}

export interface SugarResponsePagination {
  currentPage: number
  limit: number
  totalItems: number
  totalPages: number
}

export interface SugarPaginatedResponse<T> extends SugarResponse<T[]> {
  pagination: SugarResponsePagination
}

export enum SugarPaymentSetupType {
  ACH = 'ach',
  W9 = 'w9'
}
export enum SugarPaymentSetupLlcClassification {
  C = 'c',
  S = 's',
  P = 'p'
}
export enum SugarPaymentSetupTinType {
  EIN = 'ein',
  SSN = 'ssn'
}
export enum SugarPaymentSetupTaxClassificationCategory {
  INDIVIDUAL = 'individual',
  C = 'c',
  S = 's',
  PARTNERSHIP = 'partnership',
  TRUST = 'trust',
  LLC = 'llc',
  OTHER = 'other'
}

export enum SugarPaymentSetupStatus {
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  PRENOTE_SUBMITTED = 'prenote_submitted',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}
export enum SugarPaymentSetupAccountType {
  CHECKING = 'checking',
  SAVINGS = 'savings'
}
export enum SugarPaymentSetupEntityType {
  BUSINESS = 'business',
  PROVIDER = 'provider'
}

// The five fields the two GET payloads and the confirm payload all share.
// `entityType` is deliberately NOT here: both GETs return it, the confirm response does not.
export interface SugarPaymentSetupConfirmable {
  confirmedByContactId: string | null
  confirmedByName: string | null
  confirmedDate: string | null
  status: SugarPaymentSetupStatus | null
  statusDisplay: string | null
}

export interface SugarPaymentSetupAch extends SugarPaymentSetupConfirmable {
  accountName: string | null
  accountNumberMasked: string | null
  accountType: SugarPaymentSetupAccountType | null
  accountTypeDisplay: string | null
  bankName: string | null
  entityType: SugarPaymentSetupEntityType
  routingNumberMasked: string | null
}

export interface SugarPaymentSetupW9 extends SugarPaymentSetupConfirmable {
  entityType: SugarPaymentSetupEntityType
  llcClassification: SugarPaymentSetupLlcClassification | null
  llcClassificationDisplay: string | null
  taxAddress: {
    city: string | null
    postalCode: string | null
    state: string | null
    street: string | null
  }
  taxClassification: SugarPaymentSetupTaxClassificationCategory | null
  taxClassificationDisplay: string | null
  taxName: string | null
  tinMasked: string | null
  tinType: SugarPaymentSetupTinType | null
  tinTypeDisplay: string | null
}

export interface SugarPaymentSetupConfirmation extends SugarPaymentSetupConfirmable {
  previouslyConfirmedDate: string | null
  type: SugarPaymentSetupType
}

export type SugarPaymentSetupRequest = SugarRequest & { paymentSetupType: SugarPaymentSetupType }
export type SugarPaymentSetupConfirmResponse = SugarResponse<SugarPaymentSetupConfirmation | null>

// Metadata only for a newer submission still awaiting staff review. Unapproved values are never returned;
// `data` keeps the approved details. Optional because older Sugar builds omit the key entirely.
export interface SugarPaymentSetupPendingSubmission {
  status: SugarPaymentSetupStatus
  statusDisplay: string | null
  submittedDate: string
}

export type SugarPaymentSetupViewResponse<T> = SugarResponse<T | null> & {
  pendingSubmission?: SugarPaymentSetupPendingSubmission | null
}

export interface SugarPaymentSetupInput {
  authorizationAcknowledgement: boolean
  file?: File | undefined
  nameAuthorizedSigner: string
  paymentSetupType: SugarPaymentSetupType
  titleAuthorizedSigner: string
}

export interface SugarPaymentSetupACHInput extends SugarPaymentSetupInput {
  bankAccountNumber: string
  bankAccountType: string
  bankName: string
  bankRoutingNumber: string
  nameOfAccount: string
  paymentSetupType: SugarPaymentSetupType.ACH
}

export interface SugarPaymentSetupW9Input extends SugarPaymentSetupInput {
  llcClassification: SugarPaymentSetupLlcClassification
  paymentSetupType: SugarPaymentSetupType.W9
  taxAddress: {
    city: string
    postalCode: string
    state: string
    street: string
  }
  taxClassificationCategory: SugarPaymentSetupTaxClassificationCategory
  taxIdentificationNumber: string
  taxName: string
  tinType: SugarPaymentSetupTinType
}

export interface SugarPaymentReport {
  categoryId: string
  dayPaid: string
  documentRevisionId: string
  fileExt: string
  fileMimeType: string
  fileName: string
  fileSize: number
  id: string
  payFileId: string
  paymentMonth: number
  subCategoryId: string
  paymentMonthDisplay: string
  reportDisplayName: string
}

export interface SugarProfile {
  email: string
  firstName: string
  id: string
  lastName: string
}

export interface SugarOption {
  label: string
  value: string
}

export interface SugarOptionWithHelp extends SugarOption {
  help: string
}

export interface SugarExpenditureSection extends SugarOption {
  expenditureCategory: SugarOptionWithHelp[]
}

export interface SugarExpenditureLineItem {
  id?: string | null
  expenditureSection: string
  expenditureCategory: string
  expenditureAmount: number
  expenditureNotes: string
}

export interface SugarExpenditure {
  expenditureLineItems?: SugarExpenditureLineItem[]
  id?: string | null
  locked: boolean
  lockedDate: string | null
  lockedForEditing: boolean
  name: string
  providerId: string
  quarterId: string
  reportedCompleteDate: string | null
  schoolYearId: string
  totalExpenditures: number
}

export type SugarExpenditureInput = Omit<
  SugarExpenditure,
  'locked' | 'name' | 'reportedCompleteDate' | 'totalExpenditures'
>

export interface SugarQuarter extends SugarOption {
  default: boolean
  locked: boolean
  name: string
}

export interface SugarSchoolYear extends SugarOption {
  default: boolean
  locked: boolean
  name: string
}

export interface SugarExpenditureSchoolYear extends SugarSchoolYear {
  quarters: SugarQuarter[]
}

export interface SugarProfile {
  email: string
  firstName: string
  id: string
  isBusiness: boolean
  isCdec: boolean
  isLco: boolean
  isProvider: boolean
  lastName: string
}

export interface SugarProfileInput {
  firstName?: string
  lastName?: string
}

export interface SugarEnrollmentReportingSchoolYearOption extends SugarOption {
  default: boolean
  locked: boolean
}

export type SugarEnrollmentType = 'in_district' | 'charter_community_partner'
export type SugarEnrollmentTypeOption = {
  label: string
  value: SugarEnrollmentType
}

export type SugarYesNo = 'Yes' | 'No'

export type SugarYesNoOption = {
  label: string
  value: SugarYesNo
}

export type SugarStudentEthnicity = 'hispanic_latino' | 'not_hispanic_latino' | 'prefer_not_to_answer'

export type SugarStudentEthnicityOption = {
  label: string
  value: SugarStudentEthnicity
}

export type SugarStudentIncomeLevel = '0_100_fpl' | '101_270_fpl' | '270_plus_fpl' | 'na'
export type SugarStudentIncomeLevelOption = {
  label: string
  value: SugarStudentIncomeLevel
}

export type SugarStudentRace =
  | 'american_indian_alaska_native'
  | 'asian'
  | 'black_african_american'
  | 'native_hawiian_other_pacific_islander'
  | 'white'
  | 'other'
  | 'prefer_not_to_answer'
export type SugarStudentRaceOption = {
  label: string
  value: SugarStudentRace
}

export interface SugarOptions {
  bankAccountType: SugarOption[]
  businessProviders: SugarOption[]
  enrollmentReportingSchoolYears: SugarEnrollmentReportingSchoolYearOption[]
  enrollmentType: SugarEnrollmentTypeOption[]
  expenditureReportingSchoolYears: SugarExpenditureSchoolYear[]
  expenditureSection: SugarExpenditureSection[]
  schoolYears: SugarSchoolYear[]
  studentDualLanguageLearner: SugarYesNoOption[]
  studentEthnicity: SugarStudentEthnicityOption[]
  studentExperiencingHomelessness: SugarYesNoOption[]
  studentFosterCare: SugarYesNoOption[]
  studentIncomeLevel: SugarStudentIncomeLevelOption[]
  studentRace: SugarStudentRaceOption[]
}

export type SugarOptionName = keyof SugarOptions

export interface SugarMe {
  profile: SugarProfile
  options: SugarOptions
  permissions: {
    showDocuments: boolean
    showExpenditureReporting: boolean
    showPaymentSetup: boolean
    showEnrollmentReporting: boolean
  }
  uploadTypes: SugarUploadType[]
}

export interface SugarUploadTypeOption {
  optionsList: SugarOptionName
  required: boolean
  formField: string
  displayName: string
}

export interface SugarUploadType {
  allowNotes: boolean
  displayName: string
  helperText: string
  multiple: boolean
  mimeTypes: string[]
  options: SugarUploadTypeOption[]
  templateId: string
  type: string
}

export type SugarExpenditureType = 'xlsx' | 'pdf'
export type SugarDocumentType = 'report' | 'template' | 'other'

export interface SugarDocument {
  categoryId: string
  categoryDisplay: string
  dayPaid: string
  dayPaidUnixTimestamp: number
  documentRevisionId: string
  documentsActiveDate: string
  fileExt: string
  fileMimeType: string
  fileName: string
  fileSize: number
  id: string
  paymentMonth: number
  paymentMonthDisplay: string
  reportDisplayName: string
  status: string | null
  subCategoryId: string
  subCategoryDisplay: string
}

export type SugarExpendituresFilters = {
  schoolYearId?: string
  quarterId?: string
  providerId?: string
}

export type SugarExpenditureParams = {
  schoolYearId: string
  quarterId: string
  providerId?: string
}

export type SugarStudentAttestation = {
  enrollmentEndDate: string | null
  enrollmentId: string
  enrollmentStartDate: string
  paymentLocked?: boolean | null
  providerLicenseNumber: string
  providerName: string
  studentChildPaymentId: string
  studentChildUpkId: string
  studentFirstName: string
  studentLastName: string
  studentAttestationId: string | null
  attended: boolean | null
  locked: boolean | null
}

export type SugarStudentAttestationInput = Pick<
  SugarStudentAttestation,
  'attended' | 'enrollmentEndDate' | 'enrollmentId' | 'studentAttestationId'
>

export type SugarAttestationPeriod = {
  attestationId: string | null
  attestationLocked: boolean | null
  attestationPeriodEndDate: string
  attestationPeriodId: string
  attestationPeriodLocked: boolean
  attestationPeriodName: string
  attestationPeriodStartDate: string
  schoolYearId: string
  studentAttestations: SugarStudentAttestation[]
}

export type SugarAttestationPeriodInput = Pick<SugarAttestationPeriod, 'attestationId' | 'attestationPeriodId'> & {
  studentAttestations: SugarStudentAttestationInput[]
}

export type SugarEnrollmentParams = {
  schoolYearId?: string
}

export interface SugarEnrollment {
  enrollmentEndDate?: string | null
  enrollmentEnrolledHours: number
  enrollmentId: string
  enrollmentIepHours: number
  enrollmentStartDate: string
  enrollmentStatus: string
  // Sugar returns this integer field as a string
  enrollmentTotalFundedHours: string
  enrollmentType: SugarEnrollmentType
  locked: boolean
  providerId: string
  providerLicenseNumber: string
  providerName: string
  schoolYearId: string
  schoolYearName: string
  studentChildPaymentId: string
  studentChildUpkId: string
  studentDob: string
  studentDualLanguageLearner: SugarYesNo
  studentEthnicity: SugarStudentEthnicity
  studentExperiencingHomelessness: SugarYesNo
  studentFirstName: string
  studentFosterCare: SugarYesNo
  studentHouseholdSize: number
  studentIncomeLevel: SugarStudentIncomeLevel
  studentLastName: string
  studentRace: SugarStudentRace | null
}

export type SugarEnrollmentInput = Partial<
  Omit<
    SugarEnrollment,
    'providerName' | 'studentChildPaymentId' | 'enrollmentStatus' | 'schoolYearName' | 'enrollmentTotalFundedHours'
  > & {
    enrollmentTotalFundedHours: number
  }
>

export interface SugarContact {
  id: string
  name: string
}

export type SugarContent = {
  bulletin: {
    content: string
    id: string
    label: string
    link: null
    type: 'bulletin'
  }[]
  link: {
    content: string | null
    id: string
    label: string
    link: string
    type: 'link'
  }[]
}

export type SugarContentResponse = SugarResponse<SugarContent>
export type SugarFindExpenditureRequest = SugarRequest & SugarExpenditureParams
export type SugarFindExpenditureResponse = SugarResponse<SugarExpenditure | null>
export type SugarEditExpenditureResponse = SugarResponse<SugarExpenditure>
export type SugarCompleteExpenditureRequest = SugarRequest & { expenditureId: string }
export type SugarCompleteExpenditureResponse = SugarResponse<SugarExpenditure>
export type SugarSaveExpenditureRequest = SugarRequest & { expenditure: SugarExpenditureInput }
export type SugarSaveExpenditureResponse = SugarResponse<SugarExpenditure>
export type SugarUnlockExpenditureRequest = SugarRequest & { expenditureId: string }
export type SugarUnlockExpenditureResponse = SugarResponse<SugarExpenditure>
export type SugarExpendituresPaginatedRequest = SugarPaginatedRequest & SugarExpendituresFilters
export type SugarExpendituresPaginatedResponse = SugarPaginatedResponse<SugarExpenditure>
export type SugarDocumentsPaginatedResponse = SugarPaginatedResponse<SugarDocument>
export type SugarPaymentReportsPaginatedResponse = SugarPaginatedResponse<SugarPaymentReport>
export type SugarAttestationPeriodsPaginatedRequest = SugarPaginatedRequest & SugarEnrollmentParams
export type SugarAttestationPeriodsPaginatedResponse = SugarPaginatedResponse<SugarAttestationPeriod>
export type SugarAttestationPeriodRequest = SugarRequest & {
  attestationPeriodId: string
}
export type SugarAttestationPeriodResponse = SugarResponse<SugarAttestationPeriod>
export type SugarSaveAttestationPeriodRequest = SugarRequest & {
  attestation: SugarAttestationPeriodInput
}
export type SugarSaveAttestationPeriodResponse = SugarResponse<SugarAttestationPeriod>
export type SugarEnrollmentsPaginatedRequest = SugarPaginatedRequest & SugarEnrollmentParams
export type SugarEnrollmentsPaginatedResponse = SugarPaginatedResponse<SugarEnrollment>
export type SugarSaveEnrollmentRequest = SugarRequest & {
  enrollment: SugarEnrollmentInput
}
export type SugarSaveEnrollmentResponse = SugarResponse<SugarEnrollment>
